import React, { useState, useRef } from 'react';
import { kitchenService } from '../../services';
import upload from '../../assets/images/registration/upload.png';
import { toast } from 'react-toastify';
import { uploadImage } from '../../utils/imageUploader';

interface Form2Props {
  onNext: (data: Form2Data) => void;
  initialData?: Form2Data;
}

export interface Form2Data {
  panNumber: string;
  gstNumber: string;
  fssaiLicenseNumber: string;
  fssaiCertificate?: string;
  shopActLicense?: string;
}

const Form2: React.FC<Form2Props> = ({ onNext, initialData }) => {
  const [formData, setFormData] = useState<Form2Data>({
    panNumber: initialData?.panNumber || '',
    gstNumber: initialData?.gstNumber || '',
    fssaiLicenseNumber: initialData?.fssaiLicenseNumber || '',
    fssaiCertificate: initialData?.fssaiCertificate || '',
    shopActLicense: initialData?.shopActLicense || '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadingFssai, setUploadingFssai] = useState(false);
  const [uploadingShopAct, setUploadingShopAct] = useState(false);
  
  const fssaiInputRef = useRef<HTMLInputElement>(null);
  const shopActInputRef = useRef<HTMLInputElement>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError('');
  };

  const handleFssaiUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingFssai(true);
    try {
      const url = await uploadImage(file, 'restaurant', {
        maxSize: 5,
        allowedTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
      });
      setFormData((prev) => ({ ...prev, fssaiCertificate: url }));
      toast.success('FSSAI Certificate uploaded successfully!');
    } catch (err: any) {
      toast.error(err.message || 'Failed to upload image');
    } finally {
      setUploadingFssai(false);
    }
  };

  const handleShopActUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingShopAct(true);
    try {
      const url = await uploadImage(file, 'restaurant', {
        maxSize: 5,
        allowedTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
      });
      setFormData((prev) => ({ ...prev, shopActLicense: url }));
      toast.success('Shop Act License uploaded successfully!');
    } catch (err: any) {
      toast.error(err.message || 'Failed to upload image');
    } finally {
      setUploadingShopAct(false);
    }
  };

  const removeFssaiImage = () => {
    setFormData((prev) => ({ ...prev, fssaiCertificate: '' }));
    if (fssaiInputRef.current) {
      fssaiInputRef.current.value = '';
    }
  };

  const removeShopActImage = () => {
    setFormData((prev) => ({ ...prev, shopActLicense: '' }));
    if (shopActInputRef.current) {
      shopActInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.panNumber.trim()) {
      setError('PAN Number is required');
      return;
    }
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.panNumber.trim())) {
      setError('Please enter a valid PAN Number (e.g., ABCDE1234F)');
      return;
    }
    if (!formData.gstNumber.trim()) {
      setError('GST Number is required');
      return;
    }
    if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(formData.gstNumber.trim())) {
      setError('Please enter a valid GST Number');
      return;
    }
    if (!formData.fssaiLicenseNumber.trim()) {
      setError('FSSAI License Number is required');
      return;
    }
    if (!/^[0-9]{14}$/.test(formData.fssaiLicenseNumber.trim())) {
      setError('Please enter a valid 14-digit FSSAI License Number');
      return;
    }

    setLoading(true);
    try {
      // Submit to API
      const response: any = await kitchenService.submitStep2(formData);
      toast.success(response.message || 'Legal details saved successfully!');
      // Move to next step
      onNext(formData);
    } catch (err: any) {
      setError(err.message || 'Failed to save data. Please try again.');
      toast.error(err.message || 'Failed to save legal details');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="">
      <div className="w-full">
        <h2 className="text-[35px] font-Urbanist font-semibold text-gray-900 mb-2 text-left leading-[125%]">
          Legal & Compliance
        </h2>
        <p className="text-[#6A6A6A] text-[15px] font-medium leading-[140%]">
          Enter these details to get started
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-5 mt-[20px]">
          {/* PAN Number */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="panNumber"
              className="text-sm font-Urbanist font-medium text-gray-700"
            >
              PAN Number
            </label>
            <input
              type="text"
              id="panNumber"
              name="panNumber"
              value={formData.panNumber}
              onChange={handleChange}
              placeholder="Enter PAN Number"
              className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all uppercase"
              required
              maxLength={10}
            />
          </div>

          {/* GST Number */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="gstNumber"
              className="text-sm font-Urbanist font-medium text-gray-700"
            >
              GST Number
            </label>
            <input
              type="text"
              id="gstNumber"
              name="gstNumber"
              value={formData.gstNumber}
              onChange={handleChange}
              placeholder="Enter GST Number"
              className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all uppercase"
              required
              maxLength={15}
            />
          </div>

          {/* FSSAI License Number */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="fssaiLicenseNumber"
              className="text-sm font-Urbanist font-medium text-gray-700"
            >
              FSSAI License Number
            </label>
            <input
              type="text"
              id="fssaiLicenseNumber"
              name="fssaiLicenseNumber"
              value={formData.fssaiLicenseNumber}
              onChange={handleChange}
              placeholder="Enter FSSAI License Number"
              className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all"
              required
              maxLength={14}
            />
          </div>

          {/* FSSAI Certificate */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="fssaiCertificate"
              className="text-sm font-Urbanist font-medium text-gray-700"
            >
              FSSAI Certificate
            </label>
            <div className="relative flex items-center gap-2">
              <input
                type="text"
                id="fssaiCertificate"
                name="fssaiCertificate"
                value={formData.fssaiCertificate}
                onChange={handleChange}
                placeholder="Upload FSSAI Certificate"
                className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all"
                readOnly
                onFocus={(e) => e.target.blur()}
              />
              <input
                type="file"
                ref={fssaiInputRef}
                onChange={handleFssaiUpload}
                accept="image/jpeg,image/jpg,image/png,image/webp"
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fssaiInputRef.current?.click()}
                disabled={uploadingFssai}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-gray-50 transition-all disabled:opacity-50"
              >
                {uploadingFssai ? (
                  <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <img src={upload} alt="Upload" className="w-[30px] h-[30px]" />
                )}
              </button>
            </div>
            {formData.fssaiCertificate && (
              <div className="relative mt-2 inline-block">
                <img
                  src={formData.fssaiCertificate}
                  alt="FSSAI Certificate"
                  className="w-full max-w-[8rem] h-auto rounded-lg border border-gray-200"
                />
                <button
                  type="button"
                  onClick={removeFssaiImage}
                  className="absolute -top-[7px] right-[13rem] w-6 h-6 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white rounded-full transition-all"
                >
                  ×
                </button>
              </div>
            )}
          </div>

          {/* Shop Act License */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="shopActLicense"
              className="text-sm font-Urbanist font-medium text-gray-700"
            >
              Shop Act License
            </label>
            <div className="relative flex items-center gap-2">
              <input
                type="text"
                id="shopActLicense"
                name="shopActLicense"
                value={formData.shopActLicense}
                onChange={handleChange}
                placeholder="Upload Shop Act License"
                className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all"
                readOnly
                onFocus={(e) => e.target.blur()}
              />
              <input
                type="file"
                ref={shopActInputRef}
                onChange={handleShopActUpload}
                accept="image/jpeg,image/jpg,image/png,image/webp"
                className="hidden"
              />
              <button
                type="button"
                onClick={() => shopActInputRef.current?.click()}
                disabled={uploadingShopAct}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-gray-50 transition-all disabled:opacity-50"
              >
                {uploadingShopAct ? (
                  <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <img src={upload} alt="Upload" className="w-[30px] h-[30px]" />
                )}
              </button>
            </div>
            {formData.shopActLicense && (
              <div className="relative mt-2 inline-block">
                <img
                  src={formData.shopActLicense}
                  alt="Shop Act License"
                  className="w-full max-w-[8rem] h-auto rounded-lg border border-gray-200"
                />
                <button
                  type="button"
                  onClick={removeShopActImage}
                  className="absolute -top-[7px] right-[13rem] w-6 h-6 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white rounded-full transition-all"
                >
                  ×
                </button>
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm mt-2">
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 mt-2">
            <button
              type="submit"
              className="w-full px-6 py-3.5 text-base font-Urbanist font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-400 rounded-lg hover:from-orange-600 hover:to-orange-500 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 active:scale-[0.98]"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Next'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Form2;
